import { useState, useEffect, useCallback } from "react";
import { collection, doc, getDoc, getDocs, query, where, addDoc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";
import { POSITIONS, SITUATION_TYPES } from "../constants";
import { calcLevel, getAccuracyColor } from "../utils";
import { ScenarioForm } from "./ScenarioForm";
import { TacticalBoard } from "./TacticalBoard";

function CoachDashboard({ userProfile, onSignOut }) {
  const [tab,setTab]=useState("players"),[team,setTeam]=useState(null),[members,setMembers]=useState([]);
  const [logs,setLogs]=useState([]),[loading,setLoading]=useState(true),[copied,setCopied]=useState(null);
  const [selectedPlayerId,setSelectedPlayerId]=useState(null);
  const [leaderboardPos,setLeaderboardPos]=useState("all");
  const [assigningFor,setAssigningFor]=useState(null),[pendingPositions,setPendingPositions]=useState([]);
  const [assigningScenariosFor,setAssigningScenariosFor]=useState(null),[pendingScenarioIds,setPendingScenarioIds]=useState([]);
  const [customScenarios,setCustomScenarios]=useState([]),[showScenarioForm,setShowScenarioForm]=useState(false),[editingScenario,setEditingScenario]=useState(null);
  const [savedPlays,setSavedPlays]=useState([]),[showPlayForm,setShowPlayForm]=useState(false),[editingPlay,setEditingPlay]=useState(null);

  const loadData=useCallback(async()=>{
    if(!userProfile?.teamId)return; setLoading(true);
    const[ts,us,ls,cs,ps]=await Promise.all([
      getDoc(doc(db,"teams",userProfile.teamId)),
      getDocs(query(collection(db,"users"),where("teamId","==",userProfile.teamId))),
      getDocs(query(collection(db,"playLogs"),where("teamId","==",userProfile.teamId))),
      getDocs(query(collection(db,"customScenarios"),where("teamId","==",userProfile.teamId))),
      getDocs(query(collection(db,"savedPlays"),where("teamId","==",userProfile.teamId))),
    ]);
    setTeam({id:ts.id,...ts.data()});
    setMembers(us.docs.map(d=>({id:d.id,...d.data()})).filter(m=>m.id!==userProfile.id));
    setLogs(ls.docs.map(d=>({id:d.id,...d.data()})));
    setCustomScenarios(cs.docs.map(d=>({id:d.id,...d.data()})));
    setSavedPlays(ps.docs.map(d=>({id:d.id,...d.data()})));
    setLoading(false);
  },[userProfile]);

  useEffect(()=>{loadData();},[loadData]);

  const handleRemove=async(mid)=>{
    if(!window.confirm("Remove this person from the team?"))return;
    await updateDoc(doc(db,"users",mid),{teamId:null});
    setMembers(m=>m.filter(x=>x.id!==mid));
  };

  const handleSavePositions=async(pid)=>{
    await updateDoc(doc(db,"users",pid),{assignedPositions:pendingPositions});
    setMembers(m=>m.map(x=>x.id===pid?{...x,assignedPositions:pendingPositions}:x));
    setAssigningFor(null);
  };

  const handleSaveScenarioAssignments=async(pid)=>{
    await updateDoc(doc(db,"users",pid),{assignedScenarioIds:pendingScenarioIds});
    setMembers(m=>m.map(x=>x.id===pid?{...x,assignedScenarioIds:pendingScenarioIds}:x));
    setAssigningScenariosFor(null);
  };

  const handleSaveScenario=async(scenarioData)=>{
    if(editingScenario){
      await updateDoc(doc(db,"customScenarios",editingScenario.id),scenarioData);
      setCustomScenarios(cs=>cs.map(c=>c.id===editingScenario.id?{...c,...scenarioData}:c));
    }else{
      const ref=await addDoc(collection(db,"customScenarios"),{
        ...scenarioData, teamId:userProfile.teamId, createdBy:userProfile.id, createdAt:new Date().toISOString(),
      });
      setCustomScenarios(cs=>[...cs,{id:ref.id,...scenarioData,teamId:userProfile.teamId,createdBy:userProfile.id}]);
    }
    setShowScenarioForm(false); setEditingScenario(null);
  };

  const handleDeleteScenario=async(id)=>{
    if(!window.confirm("Delete this scenario? Players it's assigned to will no longer see it."))return;
    await deleteDoc(doc(db,"customScenarios",id));
    setCustomScenarios(cs=>cs.filter(c=>c.id!==id));
  };

  const handleSavePlay=async(playData)=>{
    if(editingPlay){
      await updateDoc(doc(db,"savedPlays",editingPlay.id),playData);
      setSavedPlays(ps=>ps.map(p=>p.id===editingPlay.id?{...p,...playData}:p));
    }else{
      const ref=await addDoc(collection(db,"savedPlays"),{
        ...playData, teamId:userProfile.teamId, createdBy:userProfile.id, createdAt:new Date().toISOString(),
      });
      setSavedPlays(ps=>[...ps,{id:ref.id,...playData,teamId:userProfile.teamId,createdBy:userProfile.id}]);
    }
    setShowPlayForm(false); setEditingPlay(null);
  };

  const handleDeletePlay=async(id)=>{
    if(!window.confirm("Delete this play?"))return;
    await deleteDoc(doc(db,"savedPlays",id));
    setSavedPlays(ps=>ps.filter(p=>p.id!==id));
  };

  const copyCode=(code,type)=>{ navigator.clipboard.writeText(code||""); setCopied(type); setTimeout(()=>setCopied(null),2000); };

  const getStats=(pid)=>{
    const pl=logs.filter(l=>l.userId===pid), correct=pl.filter(l=>l.correct).length;
    return {total:pl.length, correct, accuracy:pl.length>0?Math.round((correct/pl.length)*100):0, positions:[...new Set(pl.map(l=>l.position))], level:calcLevel(correct)};
  };

  const getSessionHistory=(pid)=>{
    const pl=logs.filter(l=>l.userId===pid).sort((a,b)=>b.createdAt.localeCompare(a.createdAt));
    const byDate={};
    pl.forEach(log=>{const date=log.createdAt.substring(0,10);if(!byDate[date])byDate[date]=[];byDate[date].push(log);});
    return Object.entries(byDate).map(([date,dl])=>({date,total:dl.length,correct:dl.filter(l=>l.correct).length,positions:[...new Set(dl.map(l=>l.position))]})).slice(0,10);
  };

  const MIN_TYPE_SAMPLE=3;
  const getWeakTypes=(pid)=>{
    const pl=logs.filter(l=>l.userId===pid&&l.situationType);
    const byType={};
    pl.forEach(log=>{
      if(!byType[log.situationType])byType[log.situationType]={total:0,correct:0};
      byType[log.situationType].total++;
      if(log.correct)byType[log.situationType].correct++;
    });
    return Object.entries(byType).map(([type,s])=>({
      type, label:SITUATION_TYPES.find(t=>t.id===type)?.label||type,
      total:s.total, correct:s.correct, accuracy:Math.round((s.correct/s.total)*100),
    })).filter(s=>s.total>=MIN_TYPE_SAMPLE).sort((a,b)=>a.accuracy-b.accuracy).slice(0,3);
  };

  const players=members.filter(m=>m.role==="player");
  const coaches=members.filter(m=>m.role==="assistant_coach");
  const selectedPlayer=selectedPlayerId?members.find(m=>m.id===selectedPlayerId):null;

  const MIN_HELP_SAMPLE=5, LOW_ACCURACY=60, INACTIVE_DAYS=7;
  const getNeedsHelp=()=>{
    const now=Date.now();
    return players.map(p=>{
      const s=getStats(p.id);
      const pl=logs.filter(l=>l.userId===p.id).sort((a,b)=>b.createdAt.localeCompare(a.createdAt));
      const lastPlayed=pl[0]?.createdAt||null;
      const daysSince=lastPlayed?Math.floor((now-new Date(lastPlayed).getTime())/86400000):null;
      const reasons=[];
      if(s.total>=MIN_HELP_SAMPLE&&s.accuracy<LOW_ACCURACY)reasons.push(`${s.accuracy}% accuracy`);
      if(daysSince!==null&&daysSince>=INACTIVE_DAYS)reasons.push(`inactive ${daysSince}d`);
      if(s.total===0)reasons.push("hasn't started");
      return{...p,stats:s,daysSince,reasons};
    }).filter(p=>p.reasons.length>0).sort((a,b)=>a.stats.accuracy-b.stats.accuracy);
  };
  const needsHelp=getNeedsHelp();

  const getLeaderboard=()=>[...players].map(p=>{
    const pl=leaderboardPos==="all"?logs.filter(l=>l.userId===p.id):logs.filter(l=>l.userId===p.id&&l.position===leaderboardPos);
    const correct=pl.filter(l=>l.correct).length;
    return{...p,correct,total:pl.length,level:calcLevel(correct),accuracy:pl.length>0?Math.round((correct/pl.length)*100):0};
  }).filter(p=>leaderboardPos==="all"||p.total>0).sort((a,b)=>b.level!==a.level?b.level-a.level:b.correct-a.correct);

  // ── Player detail view ────────────────────────────────────────────────────
  if (selectedPlayer) {
    const sessions=getSessionHistory(selectedPlayerId), s=getStats(selectedPlayerId), weakTypes=getWeakTypes(selectedPlayerId);
    return (
      <div style={{padding:"16px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <button onClick={()=>setSelectedPlayerId(null)} style={{fontSize:12,color:"#231F20",background:"none",border:"none",cursor:"pointer",padding:0,fontWeight:600}}>← Back to team</button>
          <button onClick={onSignOut} style={{fontSize:11,color:"#888",background:"none",border:"1px solid #ddd",borderRadius:5,padding:"4px 10px",cursor:"pointer"}}>Sign Out</button>
        </div>
        <div style={{background:"#231F20",borderRadius:10,padding:"14px 16px",marginBottom:16}}>
          <div style={{fontSize:16,fontWeight:700,color:"#1FD71D",marginBottom:10}}>{selectedPlayer.playerName}</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8}}>
            {[{label:"Level",val:`⭐ ${s.level}`},{label:"Played",val:s.total},{label:"Correct",val:s.correct},{label:"Accuracy",val:`${s.accuracy}%`}].map(x=>(
              <div key={x.label} style={{textAlign:"center"}}>
                <div style={{fontSize:9,color:"#5fcf70",textTransform:"uppercase",marginBottom:2}}>{x.label}</div>
                <div style={{fontSize:16,fontWeight:700,fontFamily:"monospace",color:"#1FD71D"}}>{x.val}</div>
              </div>
            ))}
          </div>
        </div>
        {weakTypes.length>0&&(
          <div style={{marginBottom:16}}>
            <div style={{fontSize:12,fontWeight:700,color:"#888",marginBottom:10,textTransform:"uppercase",letterSpacing:"0.05em"}}>🎯 Areas to Work On</div>
            {weakTypes.map(t=>(
              <div key={t.type} style={{background:"#fff8e1",border:"1px solid #ffe082",borderRadius:8,padding:"10px 14px",marginBottom:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:"#856404"}}>{t.label}</div>
                  <div style={{fontSize:10,color:"#a17f00"}}>{t.correct}/{t.total} correct</div>
                </div>
                <div style={{fontSize:16,fontWeight:700,fontFamily:"monospace",color:getAccuracyColor(t.accuracy)}}>{t.accuracy}%</div>
              </div>
            ))}
          </div>
        )}
        <div style={{fontSize:12,fontWeight:700,color:"#888",marginBottom:10,textTransform:"uppercase",letterSpacing:"0.05em"}}>Session History</div>
        {sessions.length===0&&<div style={{textAlign:"center",padding:"20px",color:"#bbb",fontSize:13}}>No sessions yet</div>}
        {sessions.map((session,i)=>{
          const acc=session.total>0?Math.round((session.correct/session.total)*100):0, color=getAccuracyColor(acc);
          return (
            <div key={i} style={{background:"#f8f8f8",borderRadius:8,padding:"10px 14px",marginBottom:8}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                <div style={{fontSize:12,color:"#888"}}>{new Date(session.date).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}</div>
                <div style={{fontSize:15,fontWeight:700,fontFamily:"monospace",color}}>{acc}%</div>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                <div style={{fontSize:11,color:"#aaa"}}>{session.correct}/{session.total} correct</div>
                <div style={{display:"flex",gap:4,flexWrap:"wrap",justifyContent:"flex-end"}}>
                  {session.positions.map(p=><span key={p} style={{background:"#231F20",color:"#1FD71D",fontSize:9,fontWeight:700,padding:"1px 6px",borderRadius:999,fontFamily:"monospace"}}>{p}</span>)}
                </div>
              </div>
              <div style={{height:4,background:"#e0e0e0",borderRadius:2,overflow:"hidden"}}>
                <div style={{height:"100%",width:`${acc}%`,background:color,borderRadius:2}}/>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div style={{padding:"16px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div>
          <div style={{fontSize:16,fontWeight:700,color:"#231F20"}}>{team?.name||"Your Team"}</div>
          <div style={{fontSize:11,color:"#888"}}>Coach: {userProfile?.playerName}</div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={loadData} style={{fontSize:11,color:"#888",background:"none",border:"1px solid #ddd",borderRadius:5,padding:"4px 10px",cursor:"pointer"}}>↻</button>
          <button onClick={onSignOut} style={{fontSize:11,color:"#888",background:"none",border:"1px solid #ddd",borderRadius:5,padding:"4px 10px",cursor:"pointer"}}>Sign Out</button>
        </div>
      </div>

      <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:16}}>
        {[
          {label:"⚾ PLAYER INVITE CODE",code:team?.playerInviteCode,bg:"#e3fbe6",border:"#8fe39c",tc:"#231F20",bb:"#231F20",bc:"#1FD71D",type:"player"},
          {label:"🏆 COACH INVITE CODE", code:team?.coachInviteCode, bg:"#fff8e1",border:"#ffe082",tc:"#856404",bb:"#856404",bc:"white",  type:"coach"},
        ].map(c=>(
          <div key={c.type} style={{background:c.bg,border:`1px solid ${c.border}`,borderRadius:9,padding:"10px 14px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div>
              <div style={{fontSize:9,color:c.tc,fontWeight:700,letterSpacing:"0.06em",marginBottom:2}}>{c.label}</div>
              <div style={{fontSize:22,fontWeight:700,fontFamily:"monospace",color:c.tc,letterSpacing:"0.2em"}}>{c.code||"——"}</div>
            </div>
            <button onClick={()=>copyCode(c.code,c.type)} style={{padding:"8px 16px",background:c.bb,color:c.bc,border:"none",borderRadius:7,cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"monospace"}}>
              {copied===c.type?"Copied! ✓":"Copy"}
            </button>
          </div>
        ))}
      </div>

      <div style={{display:"flex",marginBottom:14,background:"#f0f0f0",borderRadius:8,padding:3}}>
        {[{id:"players",label:`Players (${players.length})`},{id:"coaches",label:`Asst. (${coaches.length})`},{id:"scenarios",label:`Scenarios (${customScenarios.length})`},{id:"plays",label:`Plays (${savedPlays.length})`},{id:"leaderboard",label:"🏆 Board"}].map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{flex:1,padding:"8px 4px",borderRadius:6,border:"none",cursor:"pointer",fontSize:11,fontWeight:600,background:tab===t.id?"#231F20":"transparent",color:tab===t.id?"#1FD71D":"#666",transition:"all 0.15s"}}>{t.label}</button>
        ))}
      </div>

      {loading&&<div style={{textAlign:"center",padding:"24px",color:"#888",fontSize:13}}>Loading team data...</div>}

      {tab==="players"&&!loading&&(
        <div>
          {needsHelp.length>0&&!assigningFor&&!assigningScenariosFor&&(
            <div style={{background:"#ffebee",border:"1px solid #ef9a9a",borderRadius:9,padding:"12px 14px",marginBottom:14}}>
              <div style={{fontSize:12,fontWeight:700,color:"#c62828",marginBottom:8,textTransform:"uppercase",letterSpacing:"0.05em"}}>⚠️ Needs Help ({needsHelp.length})</div>
              {needsHelp.map(p=>(
                <button key={p.id} onClick={()=>setSelectedPlayerId(p.id)} style={{display:"flex",justifyContent:"space-between",alignItems:"center",width:"100%",background:"white",border:"1px solid #ef9a9a",borderRadius:7,padding:"8px 10px",marginBottom:6,cursor:"pointer",textAlign:"left"}}>
                  <span style={{fontSize:12,fontWeight:700,color:"#231F20"}}>{p.playerName}</span>
                  <span style={{fontSize:10,color:"#c62828",fontWeight:600}}>{p.reasons.join(" · ")}</span>
                </button>
              ))}
            </div>
          )}
          {assigningFor&&(
            <div style={{background:"white",border:"1.5px solid #231F20",borderRadius:10,padding:"14px",marginBottom:14}}>
              <div style={{fontSize:13,fontWeight:700,color:"#231F20",marginBottom:10}}>Assign positions for {members.find(m=>m.id===assigningFor)?.playerName}</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6,marginBottom:12}}>
                {POSITIONS.map(p=>{
                  const active=pendingPositions.includes(p.id);
                  return <button key={p.id} onClick={()=>setPendingPositions(prev=>active?prev.filter(x=>x!==p.id):[...prev,p.id])} style={{padding:"8px 4px",borderRadius:7,border:active?"2px solid #231F20":"1px solid #ddd",background:active?"#231F20":"white",color:active?"#1FD71D":"#333",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:"monospace",transition:"all 0.15s"}}>
                    {p.id}<br/><span style={{fontSize:9,fontWeight:400}}>{p.label}</span>
                  </button>;
                })}
              </div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>handleSavePositions(assigningFor)} style={{flex:1,padding:"10px",background:"#231F20",color:"#1FD71D",border:"none",borderRadius:7,cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"monospace"}}>Save</button>
                <button onClick={()=>setAssigningFor(null)} style={{padding:"10px 14px",background:"white",color:"#888",border:"1px solid #ddd",borderRadius:7,cursor:"pointer",fontSize:13}}>Cancel</button>
              </div>
            </div>
          )}
          {assigningScenariosFor&&(
            <div style={{background:"white",border:"1.5px solid #231F20",borderRadius:10,padding:"14px",marginBottom:14}}>
              <div style={{fontSize:13,fontWeight:700,color:"#231F20",marginBottom:10}}>Assign scenarios for {members.find(m=>m.id===assigningScenariosFor)?.playerName}</div>
              {customScenarios.length===0&&<div style={{fontSize:12,color:"#888",marginBottom:12}}>No custom scenarios yet — create one in the Scenarios tab first.</div>}
              <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:12,maxHeight:220,overflowY:"auto"}}>
                {customScenarios.map(sc=>{
                  const active=pendingScenarioIds.includes(sc.id);
                  return (
                    <label key={sc.id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",borderRadius:7,border:active?"2px solid #231F20":"1px solid #ddd",background:active?"#e3fbe6":"white",cursor:"pointer"}}>
                      <input type="checkbox" checked={active} onChange={()=>setPendingScenarioIds(prev=>active?prev.filter(x=>x!==sc.id):[...prev,sc.id])} style={{width:16,height:16,cursor:"pointer"}}/>
                      <span style={{fontSize:12,fontWeight:600,color:"#333"}}>{sc.title}</span>
                      {sc.position&&<span style={{fontSize:9,fontWeight:700,color:"#231F20",background:"#1FD71D",padding:"1px 6px",borderRadius:999,fontFamily:"monospace"}}>{sc.position}</span>}
                    </label>
                  );
                })}
              </div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>handleSaveScenarioAssignments(assigningScenariosFor)} style={{flex:1,padding:"10px",background:"#231F20",color:"#1FD71D",border:"none",borderRadius:7,cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"monospace"}}>Save</button>
                <button onClick={()=>setAssigningScenariosFor(null)} style={{padding:"10px 14px",background:"white",color:"#888",border:"1px solid #ddd",borderRadius:7,cursor:"pointer",fontSize:13}}>Cancel</button>
              </div>
            </div>
          )}
          {players.length===0&&<div style={{textAlign:"center",padding:"28px",color:"#bbb",fontSize:13}}>No players yet — share your invite code to get started!</div>}
          {players.map(player=>{
            const s=getStats(player.id);
            return (
              <div key={player.id} style={{background:"#f8f8f8",borderRadius:9,padding:"12px 14px",marginBottom:8}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <button onClick={()=>setSelectedPlayerId(player.id)} style={{fontWeight:700,fontSize:14,color:"#231F20",background:"none",border:"none",cursor:"pointer",padding:0,textDecoration:"underline"}}>{player.playerName}</button>
                  <div style={{display:"flex",gap:5}}>
                    <button onClick={()=>{setAssigningFor(player.id);setPendingPositions(player.assignedPositions||[]);}} style={{fontSize:10,color:"#231F20",background:"#e3fbe6",border:"1px solid #8fe39c",borderRadius:4,padding:"2px 7px",cursor:"pointer"}}>Positions</button>
                    <button onClick={()=>{setAssigningScenariosFor(player.id);setPendingScenarioIds(player.assignedScenarioIds||[]);}} style={{fontSize:10,color:"#856404",background:"#fff8e1",border:"1px solid #ffe082",borderRadius:4,padding:"2px 7px",cursor:"pointer"}}>Scenarios</button>
                    <button onClick={()=>handleRemove(player.id)} style={{fontSize:10,color:"#e53935",background:"none",border:"1px solid #e53935",borderRadius:4,padding:"2px 7px",cursor:"pointer"}}>Remove</button>
                  </div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:5,marginBottom:8}}>
                  {[{label:"Level",val:`⭐ ${s.level}`},{label:"Played",val:s.total},{label:"Correct",val:s.correct},{label:"Accuracy",val:`${s.accuracy}%`}].map(x=>(
                    <div key={x.label} style={{background:"white",borderRadius:5,padding:"5px 4px",textAlign:"center"}}>
                      <div style={{fontSize:8,color:"#aaa",textTransform:"uppercase"}}>{x.label}</div>
                      <div style={{fontSize:12,fontWeight:700,color:"#231F20",fontFamily:"monospace"}}>{x.val}</div>
                    </div>
                  ))}
                </div>
                {s.positions.length>0&&<div style={{display:"flex",gap:4,flexWrap:"wrap",alignItems:"center",marginBottom:5}}><span style={{fontSize:9,color:"#888"}}>Practiced:</span>{s.positions.map(p=><span key={p} style={{background:"#231F20",color:"#1FD71D",fontSize:9,fontWeight:700,padding:"1px 6px",borderRadius:999,fontFamily:"monospace"}}>{p}</span>)}</div>}
                {player.assignedPositions?.length>0&&<div style={{display:"flex",gap:4,flexWrap:"wrap",alignItems:"center",marginBottom:5}}><span style={{fontSize:9,color:"#231F20",fontWeight:600}}>📋 Assigned:</span>{player.assignedPositions.map(p=><span key={p} style={{background:"#1FD71D",color:"#231F20",fontSize:9,fontWeight:700,padding:"1px 6px",borderRadius:999,fontFamily:"monospace"}}>{p}</span>)}</div>}
                {player.assignedScenarioIds?.length>0&&<div style={{display:"flex",gap:4,flexWrap:"wrap",alignItems:"center"}}><span style={{fontSize:9,color:"#856404",fontWeight:600}}>🎯 Scenarios:</span>{player.assignedScenarioIds.map(sid=>{const sc=customScenarios.find(c=>c.id===sid);return sc?<span key={sid} style={{background:"#fff8e1",color:"#856404",fontSize:9,fontWeight:700,padding:"1px 6px",borderRadius:999,border:"1px solid #ffe082"}}>{sc.title}</span>:null;})}</div>}
              </div>
            );
          })}
        </div>
      )}

      {tab==="coaches"&&!loading&&(
        <div>
          {coaches.length===0&&<div style={{textAlign:"center",padding:"28px",color:"#bbb",fontSize:13}}>No assistant coaches yet!</div>}
          {coaches.map(coach=>(
            <div key={coach.id} style={{background:"#f8f8f8",borderRadius:9,padding:"12px 14px",marginBottom:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div><div style={{fontWeight:700,fontSize:13,color:"#231F20"}}>{coach.playerName}</div><div style={{fontSize:11,color:"#888",marginTop:2}}>{coach.email}</div></div>
              <button onClick={()=>handleRemove(coach.id)} style={{fontSize:10,color:"#e53935",background:"none",border:"1px solid #e53935",borderRadius:4,padding:"2px 8px",cursor:"pointer"}}>Remove</button>
            </div>
          ))}
        </div>
      )}

      {tab==="scenarios"&&!loading&&(
        <div>
          {showScenarioForm
            ? <ScenarioForm initial={editingScenario} onSave={handleSaveScenario} onCancel={()=>{setShowScenarioForm(false);setEditingScenario(null);}}/>
            : <button onClick={()=>{setEditingScenario(null);setShowScenarioForm(true);}} style={{width:"100%",padding:"11px",background:"#231F20",color:"#1FD71D",border:"none",borderRadius:7,cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"monospace",marginBottom:14}}>+ New Scenario</button>
          }
          {customScenarios.length===0&&!showScenarioForm&&<div style={{textAlign:"center",padding:"28px",color:"#bbb",fontSize:13}}>No custom scenarios yet — build one to assign to your players!</div>}
          {customScenarios.map(sc=>(
            <div key={sc.id} style={{background:"#f8f8f8",borderRadius:9,padding:"12px 14px",marginBottom:8}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                <div style={{fontWeight:700,fontSize:13,color:"#231F20"}}>{sc.title}</div>
                <div style={{display:"flex",gap:5,flexShrink:0}}>
                  <button onClick={()=>{setEditingScenario(sc);setShowScenarioForm(true);}} style={{fontSize:10,color:"#231F20",background:"#e3fbe6",border:"1px solid #8fe39c",borderRadius:4,padding:"2px 7px",cursor:"pointer"}}>Edit</button>
                  <button onClick={()=>handleDeleteScenario(sc.id)} style={{fontSize:10,color:"#e53935",background:"none",border:"1px solid #e53935",borderRadius:4,padding:"2px 7px",cursor:"pointer"}}>Delete</button>
                </div>
              </div>
              <div style={{fontSize:11,color:"#888",marginBottom:4}}>{sc.position?`Position: ${sc.position}`:"Any position"} · Inning {sc.inning} ({sc.half})</div>
              <div style={{fontSize:12,color:"#555",lineHeight:1.5}}>{sc.situation}</div>
            </div>
          ))}
        </div>
      )}

      {tab==="plays"&&!loading&&(
        <div>
          {showPlayForm
            ? <TacticalBoard mode="edit" initial={editingPlay} onSave={handleSavePlay} onCancel={()=>{setShowPlayForm(false);setEditingPlay(null);}}/>
            : <button onClick={()=>{setEditingPlay(null);setShowPlayForm(true);}} style={{width:"100%",padding:"11px",background:"#231F20",color:"#1FD71D",border:"none",borderRadius:7,cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"monospace",marginBottom:14}}>+ New Play</button>
          }
          {savedPlays.length===0&&!showPlayForm&&<div style={{textAlign:"center",padding:"28px",color:"#bbb",fontSize:13}}>No saved plays yet — draw one up to share with your team!</div>}
          {!showPlayForm&&savedPlays.map(p=>(
            <div key={p.id} style={{background:"#f8f8f8",borderRadius:9,padding:"12px 14px",marginBottom:8}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                <div style={{fontWeight:700,fontSize:13,color:"#231F20"}}>{p.title}</div>
                <div style={{display:"flex",gap:5,flexShrink:0}}>
                  <button onClick={()=>{setEditingPlay(p);setShowPlayForm(true);}} style={{fontSize:10,color:"#231F20",background:"#e3fbe6",border:"1px solid #8fe39c",borderRadius:4,padding:"2px 7px",cursor:"pointer"}}>Edit</button>
                  <button onClick={()=>handleDeletePlay(p.id)} style={{fontSize:10,color:"#e53935",background:"none",border:"1px solid #e53935",borderRadius:4,padding:"2px 7px",cursor:"pointer"}}>Delete</button>
                </div>
              </div>
              {p.description&&<div style={{fontSize:12,color:"#555",lineHeight:1.5}}>{p.description}</div>}
            </div>
          ))}
        </div>
      )}

      {tab==="leaderboard"&&!loading&&(
        <div>
          <select value={leaderboardPos} onChange={e=>setLeaderboardPos(e.target.value)} style={{width:"100%",padding:"8px 12px",borderRadius:7,border:"1px solid #ddd",fontSize:13,color:"#333",background:"white",cursor:"pointer",marginBottom:12}}>
            <option value="all">All Positions</option>
            {POSITIONS.map(p=><option key={p.id} value={p.id}>{p.id} — {p.label}</option>)}
          </select>
          {players.length===0&&<div style={{textAlign:"center",padding:"28px",color:"#bbb",fontSize:13}}>No players yet!</div>}
          {getLeaderboard().map((p,i)=>{
            const medals=["🥇","🥈","🥉"];
            return (
              <div key={p.id} style={{background:i===0?"#fffde7":i===1?"#f5f5f5":i===2?"#fef9f0":"#f8f8f8",borderRadius:9,padding:"12px 14px",marginBottom:8,border:i===0?"1.5px solid #1FD71D":i===1?"1.5px solid #e0e0e0":i===2?"1.5px solid #ffe082":"1px solid #eee"}}>
                <div style={{display:"flex",alignItems:"center",gap:12}}>
                  <div style={{fontSize:i<3?22:13,fontWeight:700,minWidth:28,textAlign:"center",color:i>=3?"#aaa":"inherit"}}>{medals[i]||`#${i+1}`}</div>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:700,fontSize:14,color:"#231F20"}}>{p.playerName}</div>
                    <div style={{fontSize:11,color:"#aaa"}}>{p.correct} correct · {p.accuracy}%{leaderboardPos!=="all"?` at ${leaderboardPos}`:""}</div>
                  </div>
                  <div style={{textAlign:"center",background:"#231F20",borderRadius:8,padding:"5px 10px"}}>
                    <div style={{fontSize:8,color:"#5fcf70",textTransform:"uppercase"}}>Level</div>
                    <div style={{fontSize:20,fontWeight:900,fontFamily:"monospace",color:"#1FD71D",lineHeight:1}}>{p.level}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default CoachDashboard;
